/* jshint laxbreak: true */
import _ from 'lodash';
import padLeft from 'lodash.padleft';

import banzai from 'ub/control/banzai-features';

function isTransformable(asset) {
  // See https://github.com/unbounce/lp-webapp/blob/2c15a0/app/controllers/assets_controller.rb#L131-L136
  return (
    asset.content_content_type !== 'image/svg+xml' && asset.content_content_type !== 'image/gif'
  );
}

function getUrl(asset) {
  // Guard against old templates missing unique_url
  return asset.unique_url || asset.content_url;
}

function buildPublishUrl(asset, transforms) {
  const originalUrl = getUrl(asset);
  const assetSize = asset.size || {};

  const nullTransforms = [
    assetSize.width,
    assetSize.height,
    assetSize.width,
    assetSize.height,
    0,
    0,
    // Quality omitted
  ];

  const orderedTransforms = [
    // We take the max of the dimension values and 1 to ensure that we don't pass a zero to the
    // webapp for any reason, because this causes ImageMagick memory leak issues on the webapp.
    Math.max(transforms.resizeWidth, 1),
    Math.max(transforms.resizeHeight, 1),
    Math.max(transforms.cropWidth, 1),
    Math.max(transforms.cropHeight, 1),
    transforms.cropLeft,
    transforms.cropTop,
    ...(transforms.quality && transforms.quality < 100 ? [transforms.quality] : []),
  ].map(Math.round);

  // If the image is non-transformable MIME type, or if these transforms will result in the image
  // not being changed, we omit the transformation parameters, which will cause the webapp to return
  // the original, unmodified image.
  if (!isTransformable(asset) || _.isEqual(orderedTransforms, nullTransforms)) {
    return `/publish${originalUrl}`;
  }

  const urlParts = originalUrl.split('/');
  const path = urlParts.slice(0, -1).join('/');

  const filenameParts = urlParts.slice(-1)[0].split('.');
  const filenameWithoutExtension = filenameParts.slice(0, -1).join('.');
  const extension = filenameParts.slice(-1)[0];

  // Build a hash of the transform values. This will become part of the published filename.
  const transformHash = orderedTransforms.reduce(
    (acc, transform) => acc + padLeft(transform.toString(36), 3, '0'),
    ''
  );

  return (
    '/publish' +
    path +
    '/' +
    orderedTransforms.join('/') +
    '/' +
    filenameWithoutExtension +
    '_' +
    transformHash +
    '.' +
    extension
  );
}

function getImageTransforms(assetSize = {}, model, breakpoint, scale) {
  const resizedSize = model.get('geometry.transform.size', breakpoint);
  const cropSize = model.get('geometry.size', breakpoint);
  const cropPosition = model.getTransformOffsetAdjustedForInnerBorder(breakpoint);

  // These ratios scale each transform property to the desired resolution while ensuring that the
  // image will not be upscaled/enlarged in either dimension if the user has made the image element
  // larger than the original image asset. This prevents us from generating and serving
  // unnecessarily large images - instead we will let the browser upscale the image to the desired
  // element size on the published page.
  const scaleX = assetSize.width ? Math.min(scale, assetSize.width / resizedSize.width) : scale;
  const scaleY = assetSize.height ? Math.min(scale, assetSize.height / resizedSize.height) : scale;

  return {
    resizeWidth: resizedSize.width * scaleX,
    resizeHeight: resizedSize.height * scaleY,
    cropWidth: cropSize.width * scaleX,
    cropHeight: cropSize.height * scaleY,
    cropLeft: Math.abs(cropPosition.left) * scaleX,
    cropTop: Math.abs(cropPosition.top) * scaleY,
    quality: model.safeGet('geometry.transform.quality', breakpoint) || null,
  };
}

function getBackgroundCropPosition(model, resizedSize, cropSize, breakpoint) {
  const [positionX, positionY] = (
    model.safeGet('style.background.backgroundPosition', breakpoint) || 'center center'
  ).split(' ');

  const overflowX = Math.max(0, resizedSize.width - cropSize.width);
  const overflowY = Math.max(0, resizedSize.height - cropSize.height);

  let left;
  if (positionX === 'left') {
    left = 0;
  } else if (positionX === 'right') {
    left = overflowX;
  } else {
    // center
    left = overflowX / 2;
  }

  let top;
  if (positionY === 'top') {
    top = 0;
  } else if (positionY === 'bottom') {
    top = overflowY;
  } else {
    // center
    top = overflowY / 2;
  }

  return {
    left: Math.floor(left),
    top: Math.floor(top),
  };
}

function getElementSize(element, breakpoint) {
  if (element.type === 'lp-pom-root') {
    return {
      width: element.model.get('geometry.contentWidth', breakpoint),
      height: Math.max(element.calculateHeightWithTopPadding(), 1080),
    };
  } else {
    // The VisibleElementModel and subclass getSize methods do not take a breakpoint, so unlike
    // other model `get`s in this module, this relies on the page's getCurrentBreakpoint being set
    // to the correct breakpoint before getBackgroundImageTransforms is called.
    // TODO: Refactor getSize methods to take a breakpoint or re-implement each element type's
    // getSize here.
    return element.model.getSize();
  }
}

// eslint-disable-next-line complexity
function getBackgroundImageTransforms(assetSize, element, breakpoint, scale) {
  const { type, model } = element;

  const isRootElement = type === 'lp-pom-root';
  const isFullWidth = model.safeGet('geometry.fitWidthToPage', breakpoint);
  const isFixed = model.safeGet('style.background.imageFixed', breakpoint);
  const isCover = model.safeGet('style.background.imageStretched', breakpoint);

  const elementSize = getElementSize(element, breakpoint);
  const elementAspectRatio = elementSize.width / elementSize.height;
  const assetAspectRatio = assetSize.width / assetSize.height;

  let cropSize;
  let resizedSize;

  if (isCover) {
    // isCover/"fit to container" causes the image zoom to vary to cover the width and height of the
    // element (maintaining the aspect ratio). We resize the image such that it will cover the
    // element, and crop away any non-visible areas.

    // When isFullWidth/"stretch to edges" is also enabled, the element has a width of 100% of the
    // viewport. Mobile has a defined width (600px), but desktop does not, so we're picking a
    // viewport width value that should be large enough for the majority of desktop displays. For
    // users who have a larger display than this value, the visual appearance of the background
    // image will still be correct, but it will be scaled up to cover their viewport.

    // When isFixed/"parallax" is also enabled (desktop only), we do the same resizing but do not
    // crop the non-visible horizontal areas because these can become visible as the user scrolls.
    // Parallax is not allowed on mobile so we do not need to set an additional maximum viewport
    // height for mobile.
    const displaySize = {
      width: isFullWidth || isFixed || isRootElement ? breakpoint.width || 1920 : elementSize.width,
      height: isFixed ? 1080 : elementSize.height,
    };
    const displayAspectRatio = displaySize.width / displaySize.height;

    if (displayAspectRatio > assetAspectRatio) {
      const resizedWidth = Math.min(displaySize.width, assetSize.width);
      cropSize = {
        width: resizedWidth,
        height: isFixed
          ? resizedWidth / assetAspectRatio
          : resizedWidth / Math.max(assetAspectRatio, elementAspectRatio),
      };

      resizedSize = {
        width: resizedWidth,
        height: resizedWidth / assetAspectRatio,
      };
    } else {
      const resizedHeight = Math.min(displaySize.height, assetSize.height);
      cropSize = {
        width: isFixed
          ? resizedHeight * assetAspectRatio
          : resizedHeight * Math.min(assetAspectRatio, displayAspectRatio),
        height: resizedHeight,
      };

      resizedSize = {
        width: resizedHeight * assetAspectRatio,
        height: resizedHeight,
      };
    }
  } else {
    // When isCover/"fit to container" is disabled, the image shown at 100% zoom. We cannot resize
    // the image, because doing so would affect visual appearance. But we do crop away non-visible
    // areas.

    // When isFullWidth/"stretch to edges" is also enabled, the element has a width of 100% of the
    // viewport. Mobile has a defined width (600px), but desktop does not, so on desktop we keep the
    // full width of the original image and do not do any horizontal cropping, because doing so
    // would cause the image to 'run out' on displays wider than the cropped width.

    // When isFixed/"parallax" is also enabled (desktop only), we do not do any cropping in either
    // dimension because doing so would cause the image to 'run out' at certain scroll positions on
    // displays larger than the cropped dimensions.
    if (isFixed) {
      cropSize = assetSize;
    } else {
      cropSize = {
        width: Math.min(
          isFullWidth || isRootElement ? breakpoint.width || assetSize.width : elementSize.width,
          assetSize.width
        ),
        height: isRootElement ? assetSize.height : Math.min(elementSize.height, assetSize.height),
      };
    }

    resizedSize = assetSize;
  }

  const cropPosition = getBackgroundCropPosition(model, resizedSize, cropSize, breakpoint);

  return {
    resizeWidth: resizedSize.width * scale,
    resizeHeight: resizedSize.height * scale,
    cropWidth: cropSize.width * scale,
    cropHeight: cropSize.height * scale,
    cropLeft: cropPosition.left * scale,
    cropTop: cropPosition.top * scale,
    quality: null, // Not implemented for background images
  };
}

export function getMaxResolutionAvailable(assetSize, resizedSize) {
  if (!assetSize || !resizedSize) {
    return 1;
  }

  return Math.min(assetSize.width / resizedSize.width, assetSize.height / resizedSize.height);
}

function getScalesToPublish(asset, resizedSize) {
  if (
    !asset.size ||
    !resizedSize ||
    !banzai.features.isRetinaImagesEnabled() ||
    !isTransformable(asset)
  ) {
    return [1];
  }

  const ratio = Math.max(
    asset.size.width / resizedSize.width,
    asset.size.height / resizedSize.height
  );

  // This is more forgiving than getMaxResolutionAvailable: for example if the original image is
  // between 1 and 2 times larger than the element, we will still publish the 2x version. Even
  // though it will not be a true 2x, it will still be clearer on hi-res displays than just using
  // the 1x.
  if (ratio > 2) {
    return [1, 2, Math.min(ratio, 3)];
  } else if (ratio > 1) {
    return [1, ratio];
  } else {
    return [ratio];
  }
}

export function getImageElementPublishUrls(model, breakpoint) {
  const asset = model.get('content.asset', breakpoint);

  const scales = getScalesToPublish(asset, model.get('geometry.transform.size', breakpoint));

  return scales.map(scale =>
    buildPublishUrl(asset, getImageTransforms(asset.size, model, breakpoint, scale))
  );
}

export function getBackgroundPublishUrls(element, breakpoint) {
  const { model } = element;
  const asset = model.get('style.background.image', breakpoint);

  if (
    !banzai.features.isResizeBackgroundImagesEnabled() ||
    !asset.size ||
    !asset.size.width ||
    !asset.size.height
  ) {
    return [`/publish${getUrl(asset)}`];
  }

  const transforms = getBackgroundImageTransforms(asset.size, element, breakpoint, 1);
  const scales = getScalesToPublish(asset, {
    width: transforms.resizeWidth,
    height: transforms.resizeHeight,
  });

  return scales.map(scale =>
    buildPublishUrl(asset, getBackgroundImageTransforms(asset.size, element, breakpoint, scale))
  );
}



// WEBPACK FOOTER //
// ./src/control/image-publish-urls.js